import { ReasonToBuyUI } from "./ReasonToBuyUI";
import { StandardProps } from "../componentMap";
import { useVariant } from "@/hooks/useVariant/useVariant";
import { Variant } from "@/types/types";
import { TickCircleOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/TickCircleOutlined";
import { DeliveryOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/DeliveryOutlined";
import { GiftCardOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/GiftCardOutlined";
import { GuaranteesAndTroubleshootingOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/GuaranteesAndTroubleshootingOutlined";
import { pricingGroups } from "@/utils/product/getPromoMessaging";
import {
  EXCLUDED_CATEGORIES,
  EXCLUDED_PRODUCT_ID,
  EXCLUDED_PRODUCT_TYPE,
  EXCLUDED_BRANDS,
} from "@/utils/pricePromise/PricePromiseExclusions.ts";
import { ProtectPlusSvg } from "../PostAddToBasketDrawer/components/ProtectPlusCompliance/ProtectPlusSvg";
import { getScheme } from "../PostAddToBasketDrawer/scheme/schemeDecisioner";
import { replaceVariables } from "../PostAddToBasketDrawer/utils";
import { ReactNode, useMemo } from "react";

type VariantService = NonNullable<Variant["services"]>[number];
type IncludedGuaranteeService = Extract<
  VariantService,
  { __typename: "IncludedGuarantee" }
>;
type IncludedGuaranteeWithDuration = IncludedGuaranteeService & {
  duration: string;
};
type ExtendedWarrantyService = Extract<
  VariantService,
  { __typename: "ExtendedWarranty" }
>;

export type CardProps = {
  id: string;
  title: string;
  description?: string;
  descriptionHref?: string;
  icon?: ReactNode;
}[];

function isIncludedGuarantee(
  service: VariantService,
): service is IncludedGuaranteeService {
  return service.__typename === "IncludedGuarantee";
}

function isProtectPlusExtendedWarranty(
  service: VariantService,
): service is ExtendedWarrantyService {
  return (
    service.__typename === "ExtendedWarranty" &&
    service.title.includes("Protect Plus")
  );
}

function hasGuaranteeDuration(
  service: IncludedGuaranteeService | undefined,
): service is IncludedGuaranteeWithDuration {
  return !!(
    service &&
    "duration" in service &&
    typeof service.duration === "string"
  );
}

export function ReasonToBuy({ product }: Pick<StandardProps, "product">) {
  const { variant } = useVariant(product);
  const protectPlusScheme = getScheme(product);

  const includedGuarantee = variant.services?.find(isIncludedGuarantee);

  const protectPlus = useMemo(
    () => variant.services?.filter(isProtectPlusExtendedWarranty) ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [variant],
  );

  const protectPlusItem: ExtendedWarrantyService | null =
    protectPlus[0] ?? null;

  // Price promise eligibility is based on product-level information only.
  const hasExclusiveAdditional = product?.messaging?.some(
    (msg) =>
      msg.type === "additional" &&
      typeof msg.title === "string" &&
      msg.title.toLowerCase().includes("exclusive"),
  );

  const productGroupHasPromos = product?.messaging?.some(
    (msg) =>
      "promotionGroup" in msg && pricingGroups.includes(msg.promotionGroup),
  );

  const categoryIsExcluded = product?.browsePageHierarchy?.some((page) =>
    EXCLUDED_CATEGORIES.includes(page.categoryId),
  );

  const brandIsExcluded =
    typeof product?.brand?.name === "string" &&
    EXCLUDED_BRANDS.some((brand) =>
      product?.brand?.name.toLowerCase().includes(brand),
    );

  const isPricePromiseEligible = !(
    hasExclusiveAdditional ||
    categoryIsExcluded ||
    brandIsExcluded ||
    product.aliases.pimProductId === EXCLUDED_PRODUCT_ID ||
    product.__typename === EXCLUDED_PRODUCT_TYPE ||
    productGroupHasPromos
  );

  const cards: CardProps = [];
  const guaranteeTitle = hasGuaranteeDuration(includedGuarantee)
    ? `${includedGuarantee.duration} year guarantee`
    : includedGuarantee
      ? includedGuarantee.title
      : "";

  if (includedGuarantee) {
    cards.push({
      id: "guarantee",
      title: guaranteeTitle,
      description: "Included with your purchase",
      icon: <GuaranteesAndTroubleshootingOutlined size="extra-large" />,
    });
  }

  cards.push(
    {
      id: "delivery",
      title: "Free standard delivery over £50",
      description: "Otherwise £4.50",
      icon: <DeliveryOutlined size="large" />,
    },
    {
      id: "split-the-cost",
      title: "Split the cost",
      description: "Flexible payments available",
      icon: <GiftCardOutlined size="large" />,
    },
  );

  if (isPricePromiseEligible) {
    cards.push({
      id: "price-promise",
      title: "Our price promise",
      description: "Confidence in every purchase",
      descriptionHref:
        "https://www.johnlewis.com/customer-services/prices-and-payment/price-promise?source=pricepromisertb",
      icon: <TickCircleOutlined size="large" />,
    });
  }

  if (protectPlusItem) {
    const highlight = replaceVariables(
      protectPlusScheme.schemeHighlight || "",
      {
        VAR_YEARS: protectPlusItem.duration ?? "",
        VAR_PRICE: protectPlusItem.price?.display ?? "",
      },
    );
    const marketingMessage = replaceVariables(
      protectPlusScheme.schemeMarketingMessage || "",
      {
        VAR_YEARS: protectPlusItem.duration ?? "",
        VAR_PRICE: protectPlusItem.price?.display ?? "",
      },
    );

    cards.push({
      id: "protect-plus",
      title:
        highlight ||
        `Add ${protectPlusItem.duration ?? ""} years of cover for accidental damage: ${protectPlusItem.price?.display ?? ""}`,
      description:
        marketingMessage ||
        `Protect your item from accidental damage for ${protectPlusItem.duration ?? ""} years, in addition to your product's standard guarantee.`,
      icon: <ProtectPlusSvg />,
    });
  }

  return <ReasonToBuyUI cards={cards} />;
}
